/**
 * Competence matrix query + cell upsert (issue #3241).
 *
 * GET /skills/matrix requires skills.view_levels. PUT /users/:id/skills/:skillId
 * upserts a cell with If-Match on updatedAt (409 → refetch + notify).
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from './queryKeys';
import { useNotify } from '@/hooks/useNotify';
import type { CanonicalLevel, CertificateStatus, SkillCategoryKey } from '@/utils/skillLevels';
import type { SkillCertificate } from './useSkills';

export const skillMatrixQueryKey = [...queryKeys.skills.all, 'matrix'] as const;

export interface SkillMatrixSkill {
  id: string;
  code: string | null;
  name: string;
  requiresValidCertificate: boolean;
  sortOrder: number;
}

export interface SkillMatrixCategory {
  key: SkillCategoryKey | string;
  skills: SkillMatrixSkill[];
}

export interface SkillMatrixUser {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
}

export interface SkillMatrixCell {
  userId: string;
  skillId: string;
  level: CanonicalLevel | null;
  wantsToLearn: boolean;
  certificate: SkillCertificate;
  updatedAt: string;
  assignmentId?: string;
}

export interface SkillMatrix {
  categories: SkillMatrixCategory[];
  users: SkillMatrixUser[];
  cells: SkillMatrixCell[];
}

export interface UpsertSkillCellBody {
  level?: CanonicalLevel | null;
  wantsToLearn?: boolean;
  certifiedAt?: string | null;
  expiresAt?: string | null;
  certificateIssuer?: string | null;
  certificateNumber?: string | null;
  notes?: string | null;
}

export interface UpsertSkillCellVariables {
  userId: string;
  skillId: string;
  updatedAt?: string | null;
  body: UpsertSkillCellBody;
  remove?: boolean;
}

interface SkillCellWriteResponse {
  id: string;
  userId: string;
  skillId: string;
  level: CanonicalLevel | null;
  wantsToLearn: boolean;
  certificate: SkillCertificate;
  updatedAt: string;
}

type UpsertContext = {
  previous: SkillMatrix | undefined;
};

export function matrixCellKey(userId: string, skillId: string): string {
  return `${userId}:${skillId}`;
}

export function indexMatrixCells(cells: SkillMatrixCell[]): Map<string, SkillMatrixCell> {
  const map = new Map<string, SkillMatrixCell>();
  for (const cell of cells) {
    map.set(matrixCellKey(cell.userId, cell.skillId), cell);
  }
  return map;
}

function isConflict(error: unknown): boolean {
  if (error instanceof ApiError) return error.status === 409;
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === 409
  );
}

function applyOptimisticCell(matrix: SkillMatrix, variables: UpsertSkillCellVariables): SkillMatrix {
  if (variables.remove) {
    return {
      ...matrix,
      cells: matrix.cells.filter(
        (cell) => !(cell.userId === variables.userId && cell.skillId === variables.skillId),
      ),
    };
  }

  const existing = matrix.cells.find(
    (cell) => cell.userId === variables.userId && cell.skillId === variables.skillId,
  );
  const nextCell: SkillMatrixCell = {
    userId: variables.userId,
    skillId: variables.skillId,
    level: variables.body.level !== undefined ? variables.body.level : (existing?.level ?? null),
    wantsToLearn:
      variables.body.wantsToLearn !== undefined
        ? variables.body.wantsToLearn
        : (existing?.wantsToLearn ?? false),
    certificate: existing?.certificate ?? { status: 'none' as CertificateStatus, issuer: null, number: null, certifiedAt: null, expiresAt: null },
    updatedAt: existing?.updatedAt ?? new Date().toISOString(),
    ...(existing?.assignmentId !== undefined ? { assignmentId: existing.assignmentId } : {}),
  };

  if (existing) {
    return {
      ...matrix,
      cells: matrix.cells.map((cell) =>
        cell.userId === variables.userId && cell.skillId === variables.skillId ? nextCell : cell,
      ),
    };
  }
  return { ...matrix, cells: [...matrix.cells, nextCell] };
}

function cellFromWrite(response: SkillCellWriteResponse): SkillMatrixCell {
  return {
    userId: response.userId,
    skillId: response.skillId,
    level: response.level,
    wantsToLearn: response.wantsToLearn,
    certificate: response.certificate,
    updatedAt: response.updatedAt,
    assignmentId: response.id,
  };
}

export function buildUpsertSkillCellMutation(
  queryClient: QueryClient,
  notify: { error: (title: string, message?: string) => void },
  messages: { conflictTitle: string; conflictDesc: string; saveFailed: string },
) {
  return {
    mutationFn: async (variables: UpsertSkillCellVariables) => {
      const path = `/users/${variables.userId}/skills/${variables.skillId}`;
      if (variables.remove) {
        await api.delete(path);
        return null;
      }
      const headers: Record<string, string> = {};
      if (variables.updatedAt) {
        headers['If-Match'] = variables.updatedAt;
      }
      return api.put<SkillCellWriteResponse>(path, variables.body, { headers });
    },
    onMutate: async (variables: UpsertSkillCellVariables): Promise<UpsertContext> => {
      await queryClient.cancelQueries({ queryKey: skillMatrixQueryKey });
      const previous = queryClient.getQueryData<SkillMatrix>(skillMatrixQueryKey);
      if (previous) {
        queryClient.setQueryData<SkillMatrix>(
          skillMatrixQueryKey,
          applyOptimisticCell(previous, variables),
        );
      }
      return { previous };
    },
    onError: (
      error: unknown,
      _variables: UpsertSkillCellVariables,
      context: UpsertContext | undefined,
    ) => {
      if (context?.previous) {
        queryClient.setQueryData(skillMatrixQueryKey, context.previous);
      }
      if (isConflict(error)) {
        void queryClient.invalidateQueries({ queryKey: skillMatrixQueryKey });
        notify.error(messages.conflictTitle, messages.conflictDesc);
        return;
      }
      notify.error(messages.saveFailed);
    },
    onSuccess: (
      data: SkillCellWriteResponse | null,
      variables: UpsertSkillCellVariables,
    ) => {
      if (!data) return;
      queryClient.setQueryData<SkillMatrix>(skillMatrixQueryKey, (current) => {
        if (!current) return current;
        const next = cellFromWrite(data);
        const exists = current.cells.some(
          (cell) => cell.userId === variables.userId && cell.skillId === variables.skillId,
        );
        return {
          ...current,
          cells: exists
            ? current.cells.map((cell) =>
                cell.userId === variables.userId && cell.skillId === variables.skillId ? next : cell,
              )
            : [...current.cells, next],
        };
      });
    },
    onSettled: (
      _data: SkillCellWriteResponse | null | undefined,
      _error: unknown,
      variables: UpsertSkillCellVariables,
    ) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.mechanicSkills.byUser(variables.userId) });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.mechanicSkills.all, 'me'] });
    },
  };
}

export function useSkillMatrix() {
  return useQuery({
    queryKey: skillMatrixQueryKey,
    queryFn: () => api.get<SkillMatrix>('/skills/matrix'),
    staleTime: 30 * 1000,
  });
}

export function useUpsertSkillCell() {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useTranslation('hr');

  return useMutation(
    buildUpsertSkillCellMutation(queryClient, notify, {
      conflictTitle: t('skillsMatrix.conflictTitle'),
      conflictDesc: t('skillsMatrix.conflictDesc'),
      saveFailed: t('skillsMatrix.saveFailed'),
    }),
  );
}
